import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
  Loader2, Save, CalendarCog, BellRing, Gauge, Truck, Globe, Plus, Trash2, Info, Wand2,
} from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { WEEKDAY_NAMES } from '../../../lib/scheduleFormat';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC',
];

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

const SchedulingSettings = () => {
  const [form, setForm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefilling, setPrefilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await adminService.getSchedulingSettings();
      setForm(settings);
      try {
        const products = await adminService.getProducts();
        setCategories([...new Set((products || []).map((p) => p.category).filter(Boolean))].sort());
      } catch {
        setCategories([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load scheduling settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const setStandard = (index, patch) =>
    set({
      labour_standards: form.labour_standards.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });

  // Fills in every category that has no rate yet, using a baseline figure the
  // admin is expected to correct once they have timed a real batch.
  const prefillStandards = async () => {
    setPrefilling(true);
    try {
      const { rows, missing_count: missing } = await adminService.getSuggestedLabourStandards();
      if (!missing) {
        toast.info('Every product category already has a rate');
        return;
      }
      const existing = new Map(
        (form.labour_standards || [])
          .filter((row) => row.category)
          .map((row) => [row.category.trim().toLowerCase(), row]),
      );
      rows.forEach((row) => {
        const key = row.category.trim().toLowerCase();
        if (!existing.has(key) || !existing.get(key).units_per_labour_hour) {
          existing.set(key, {
            category: row.category,
            units_per_labour_hour: row.suggested_units_per_labour_hour,
          });
        }
      });
      set({ labour_standards: [...existing.values()] });
      toast.success(`Suggested a rate for ${missing} categor${missing === 1 ? 'y' : 'ies'} — correct them, then save`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not work out suggestions');
    } finally {
      setPrefilling(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminService.updateSchedulingSettings({
        operating_timezone: form.operating_timezone,
        week_start_day: Number(form.week_start_day),
        publish_horizon_weeks: Number(form.publish_horizon_weeks),
        weekly_digest_enabled: form.weekly_digest_enabled,
        weekly_digest_weekday: Number(form.weekly_digest_weekday),
        weekly_digest_time: form.weekly_digest_time,
        shift_reminder_enabled: form.shift_reminder_enabled,
        shift_reminder_time: form.shift_reminder_time,
        confirmation_deadline_hours: Number(form.confirmation_deadline_hours),
        change_batch_minutes: Number(form.change_batch_minutes),
        coverage_warning_days: Number(form.coverage_warning_days),
        coverage_critical_hours: Number(form.coverage_critical_hours),
        driver_lead_minutes: Number(form.driver_lead_minutes),
        max_hold_minutes: form.max_hold_minutes === '' || form.max_hold_minutes === null
          ? null : Number(form.max_hold_minutes),
        late_after_minutes: Number(form.late_after_minutes),
        no_show_after_minutes: Number(form.no_show_after_minutes),
        alert_email: form.alert_email || '',
        labour_standards: (form.labour_standards || [])
          .filter((row) => row.category)
          .map((row) => ({
            category: row.category,
            // Text inputs hand back strings; send the number the API expects.
            units_per_labour_hour: row.units_per_labour_hour === '' || row.units_per_labour_hour === null
              ? null : Number(row.units_per_labour_hour),
          })),
      });
      setForm(updated);
      toast.success('Scheduling settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="scheduling-settings-loading">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="scheduling-settings">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Scheduling settings</h1>
          <p className="mt-1 text-sm text-gray-600">
            How the rota behaves: the bakery clock, how far ahead you publish, and when people are chased.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700"
          data-testid="scheduling-settings-save">
          {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
          Save settings
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="h-5 w-5 text-amber-600" /> The bakery clock
          </CardTitle>
          <p className="text-sm text-gray-500">
            Every scheduling time is entered and shown in this timezone and stored in UTC, so
            daylight-saving changes and windows past midnight behave correctly. Ordering uses the same
            timezone.
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Bakery timezone">
            <select
              className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
              value={form.operating_timezone}
              onChange={(e) => set({ operating_timezone: e.target.value })}
              data-testid="settings-timezone"
            >
              {[...new Set([form.operating_timezone, ...TIMEZONES])].map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </Field>
          <Field label="Week starts on">
            <select
              className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
              value={form.week_start_day}
              onChange={(e) => set({ week_start_day: Number(e.target.value) })}
              data-testid="settings-week-start"
            >
              {WEEKDAY_NAMES.map((name, index) => (
                <option key={index} value={index}>{name}</option>
              ))}
            </select>
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CalendarCog className="h-5 w-5 text-amber-600" /> Publishing and confirmation
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <Field label="Weeks published ahead" hint="You are warned when fewer than this are published.">
            <Input type="number" min="1" max="12" value={form.publish_horizon_weeks}
              onChange={(e) => set({ publish_horizon_weeks: e.target.value })}
              data-testid="settings-publish-horizon" />
          </Field>
          <Field label="Confirm within (hours)" hint="After this, unconfirmed shifts show amber and are chased.">
            <Input type="number" min="1" max="336" value={form.confirmation_deadline_hours}
              onChange={(e) => set({ confirmation_deadline_hours: e.target.value })}
              data-testid="settings-confirm-deadline" />
          </Field>
          <Field label="Batch edits for (minutes)" hint="An editing burst becomes one message per person.">
            <Input type="number" min="1" max="120" value={form.change_batch_minutes}
              onChange={(e) => set({ change_batch_minutes: e.target.value })}
              data-testid="settings-change-batch" />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BellRing className="h-5 w-5 text-amber-600" /> Reminders and alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-3">
              <Switch checked={form.weekly_digest_enabled}
                onCheckedChange={(v) => set({ weekly_digest_enabled: v })}
                data-testid="settings-digest-enabled" />
              <span className="text-sm text-gray-800">Weekly schedule email</span>
            </div>
            <Field label="Day">
              <select
                className="mt-1 h-10 rounded-md border border-gray-300 bg-white px-2 text-sm"
                value={form.weekly_digest_weekday}
                onChange={(e) => set({ weekly_digest_weekday: Number(e.target.value) })}
                data-testid="settings-digest-weekday"
              >
                {WEEKDAY_NAMES.map((name, index) => (
                  <option key={index} value={index}>{name}</option>
                ))}
              </select>
            </Field>
            <Field label="Time">
              <Input type="time" className="mt-1 w-[7.5rem]" value={form.weekly_digest_time}
                onChange={(e) => set({ weekly_digest_time: e.target.value })}
                data-testid="settings-digest-time" />
            </Field>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-3">
              <Switch checked={form.shift_reminder_enabled}
                onCheckedChange={(v) => set({ shift_reminder_enabled: v })}
                data-testid="settings-reminder-enabled" />
              <span className="text-sm text-gray-800">Reminder the evening before a shift</span>
            </div>
            <Field label="Time">
              <Input type="time" className="mt-1 w-[7.5rem]" value={form.shift_reminder_time}
                onChange={(e) => set({ shift_reminder_time: e.target.value })}
                data-testid="settings-reminder-time" />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Coverage alert email"
              hint="Every admin gets the in-app alert; this mailbox gets the email.">
              <Input type="email" placeholder="owner@maddonutshop.com" value={form.alert_email || ''}
                onChange={(e) => set({ alert_email: e.target.value })}
                data-testid="settings-alert-email" />
            </Field>
            <Field label="Escalation warning (days out)">
              <Input type="number" min="1" max="60" value={form.coverage_warning_days}
                onChange={(e) => set({ coverage_warning_days: e.target.value })}
                data-testid="settings-coverage-warning" />
            </Field>
            <Field label="Escalation critical (hours out)">
              <Input type="number" min="1" max="720" value={form.coverage_critical_hours}
                onChange={(e) => set({ coverage_critical_hours: e.target.value })}
                data-testid="settings-coverage-critical" />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Truck className="h-5 w-5 text-amber-600" /> Drivers and attendance
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-4">
          <Field label="Driver lead time (min)" hint="Needed between pickup and the client's window.">
            <Input type="number" min="0" max="480" value={form.driver_lead_minutes}
              onChange={(e) => set({ driver_lead_minutes: e.target.value })}
              data-testid="settings-driver-lead" />
          </Field>
          <Field label="Max hold (min)" hint="Leave empty for no limit.">
            <Input type="number" min="1" max="1440" value={form.max_hold_minutes ?? ''}
              onChange={(e) => set({ max_hold_minutes: e.target.value })}
              data-testid="settings-max-hold" />
          </Field>
          <Field label="Late after (min)">
            <Input type="number" min="1" max="240" value={form.late_after_minutes}
              onChange={(e) => set({ late_after_minutes: e.target.value })}
              data-testid="settings-late-after" />
          </Field>
          <Field label="No-show after (min)">
            <Input type="number" min="1" max="480" value={form.no_show_after_minutes}
              onChange={(e) => set({ no_show_after_minutes: e.target.value })}
              data-testid="settings-no-show-after" />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Gauge className="h-5 w-5 text-amber-600" /> Labour standards
          </CardTitle>
          <p className="text-sm text-gray-500">
            Units <strong>one person</strong> can produce in an hour, per product category. Coverage
            turns this into person-hours of work and compares it with the person-hours actually
            scheduled — so 96 yeast units at 60/hour is about 1.6 hours of work, which one baker on a
            7-hour shift covers easily. Every hours-based check stays off until at least one row is
            filled in; the presence checks work regardless.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={form.labour_standards_configured
                ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                : 'border-slate-300 bg-slate-100 text-slate-700'}
              data-testid="settings-standards-state"
            >
              {form.labour_standards_configured ? 'Hours checks active' : 'Hours checks dormant'}
            </Badge>
            {!form.labour_standards_configured && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Info className="h-3.5 w-3.5" /> presence checks only
              </span>
            )}
          </div>

          {(form.labour_standards || []).map((row, index) => (
            <div key={index} className="flex flex-wrap items-end gap-2" data-testid={`standard-row-${index}`}>
              <div>
                <Label className="text-xs text-gray-500">Category</Label>
                <input
                  list="product-categories"
                  className="mt-1 h-10 w-44 rounded-md border border-gray-300 bg-white px-2 text-sm"
                  value={row.category}
                  onChange={(e) => setStandard(index, { category: e.target.value })}
                  data-testid={`standard-category-${index}`}
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Units per labour hour</Label>
                <Input
                  type="number" min="1" step="1" className="mt-1 w-40"
                  value={row.units_per_labour_hour ?? ''}
                  onChange={(e) => setStandard(index, { units_per_labour_hour: e.target.value })}
                  data-testid={`standard-units-${index}`}
                />
              </div>
              <Button
                variant="ghost" size="icon" className="mb-1 text-red-600"
                onClick={() => set({ labour_standards: form.labour_standards.filter((_, i) => i !== index) })}
                aria-label="Remove standard"
                data-testid={`standard-remove-${index}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <datalist id="product-categories">
            {categories.map((category) => <option key={category} value={category} />)}
          </datalist>

          <Button
            variant="outline"
            onClick={() => set({
              labour_standards: [...(form.labour_standards || []), { category: '', units_per_labour_hour: '' }],
            })}
            data-testid="standard-add"
          >
            <Plus className="mr-1 h-4 w-4" /> Add a category
          </Button>
          <Button
            variant="outline"
            className="ml-2"
            onClick={prefillStandards}
            disabled={prefilling}
            data-testid="standard-prefill"
          >
            {prefilling
              ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              : <Wand2 className="mr-1 h-4 w-4" />}
            Suggest starting values
          </Button>
          <p className="text-xs text-gray-500">
            Suggestions are typical small-batch figures for one person on one line, not a measurement
            of your kitchen. Time a real batch and correct them — an over-optimistic rate makes a
            short-staffed day look fine.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SchedulingSettings;
